<template>
  <el-autocomplete
      :id="id"
      :ref="id + 'Autocomplete'"
      :placeholder="placeholder"
      :fetch-suggestions="fetchSuggestions"
      :maxlength="maxlength"
      :show-word-limit="showWordLimit"
      :clearable="clearable"
      :model-value="modelValue"
      @input="handleInput"
      @update:modelValue="handleModelUpdate"
  >
    <template #default="{ item }">
      <div class="option-wrapper flex justify-between items-center">
        <span>{{ item.value }}</span>
        <el-button
          v-if="item.id"
          type="text"
          size="small"
          class="delete-suggestion-btn !p-0 !h-5 !w-5 min-h-0"
          @click.stop="handleDelete(item)"
        >
          <Icon icon="material-symbols:close" class="text-gray-400 hover:text-red-500 hover:font-bold transition-all text-lg" />
        </el-button>
      </div>
    </template>
  </el-autocomplete>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { Icon } from '@iconify/vue'
import { ElMessage } from 'element-plus'

interface Suggestion {
  id?: number
  value: string
}

export default defineComponent({
  name: 'DeletableAutocomplete',
  
  components: {
    Icon
  },

  props: {
    id: {
      type: String,
      required: true
    },
    modelValue: {
      type: String,
      required: true
    },
    placeholder: {
      type: String,
      required: true
    },
    fetchSuggestions: {
      type: Function as PropType<(query: string, callback: (data: Suggestion[]) => void) => void>,
      required: true
    },
    maxlength: {
      type: Number,
      default: 70
    },
    showWordLimit: {
      type: Boolean,
      default: true
    },
    clearable: {
      type: Boolean,
      default: true
    },
  },

  emits: ['update:modelValue', 'delete', 'input'],

  methods: {
    handleInput(value: string) {
      this.$emit('input', value)
      this.$emit('update:modelValue', value)
    },

    handleModelUpdate(value: string) {
      this.$emit('update:modelValue', value)
    },

    async handleDelete(item: Suggestion) {
      if (!item.id) return
      
      try {
        await this.$http.delete(`/helpers/suggestions/${item.id}`)
        ElMessage.success(this.$t('suggestionDeleted'))
        
        // Clear the current value if it matches the deleted suggestion
        if (this.modelValue === item.value) {
          this.handleInput('')
        }
        
        // Refetch suggestions based on the current input value
        const currentValue = this.modelValue
        await this.fetchSuggestions(currentValue, (results: Suggestion[]) => {
          // Force update the autocomplete component
          const autocomplete = this.$refs[this.id + 'Autocomplete'] as any
          if (autocomplete) {
            autocomplete.suggestions = results
          }
        })
        
        this.$emit('delete', item)
      } catch (err) {
        console.error(err)
        ElMessage.error(this.$t('deleteFailed'))
      }
    }
  }
})
</script>

<style scoped>
.delete-suggestion-btn {
  @apply opacity-50 hover:opacity-100;
}

.delete-suggestion-btn:hover {
  @apply scale-110;
}
</style> 